import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Truck } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  tax_no: "",
  tax_office: "",
  address: "",
  notes: "",
};

/**
 * Minimal "quick add supplier" dialog for use across Purchases /
 * Requirements pages. Only asks the essentials — full editing is on
 * the Suppliers page.
 */
export default function QuickSupplierDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(empty);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Tedarikçi adı zorunlu");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/suppliers", form);
      toast.success(`Tedarikçi eklendi: ${data.name}`);
      // FAZ 5 M5 fix: onCreated ÖNCE — parent state (auto-select) çalışsın, sonra kapat.
      onCreated?.(data);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quick-supplier-dialog"
        className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-500" />
            Yeni Tedarikçi
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Hızlı ekleme — detaylı düzenleme için Tedarikçi Yönetimi sayfasını kullanın.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Tedarikçi Adı *</Label>
              <Input
                data-testid="qs-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Yetkili</Label>
              <Input
                data-testid="qs-contact-input"
                value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Telefon</Label>
              <Input
                data-testid="qs-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">E-posta</Label>
              <Input
                data-testid="qs-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi No</Label>
              <Input
                data-testid="qs-tax-input"
                value={form.tax_no}
                onChange={(e) => setForm({ ...form, tax_no: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi Dairesi</Label>
              <Input
                data-testid="qs-taxoffice-input"
                value={form.tax_office}
                onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
            >
              İptal
            </Button>
            <Button
              type="submit"
              data-testid="qs-save-btn"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
